import { useState } from 'react';
import { FilterIcon, Maximize2, Minimize2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileFullscreenSearch } from './MobileFullscreenSearch';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MobileFullscreenControlsProps, SHOW_HOMEPAGE_FILTERS } from './types';

export function MobileFullscreenControls({
  isMobileFullscreen,
  showFloatingSearch,
  hasActiveFilters,
  onFullscreenToggle,
  onSearchToggle,
  onFilterToggle,
  onSearch,
  onCloseSearch
}: MobileFullscreenControlsProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!isMobile) return null;

  return (
    <>
      {/* Fullscreen Toggle Button - Safe area and focus for all devices */}
      <div className="absolute right-4 z-40 bottom-[max(1.5rem,env(safe-area-inset-bottom))]">
        <Button
          onClick={onFullscreenToggle}
          size="sm"
          className={cn(
            "h-12 w-12 p-0 rounded-full shadow-xl backdrop-blur-sm",
            "border border-white/30 transition-all duration-300",
            "min-h-[48px] touch-manipulation",
            "focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2",
            isMobileFullscreen 
              ? "bg-black/30 text-white hover:bg-black/40 shadow-black/20" 
              : "bg-white/95 text-gray-800 hover:bg-white shadow-white/20"
          )}
          aria-label={isMobileFullscreen ? "Exit fullscreen map" : "Expand map fullscreen"}
        >
          {isMobileFullscreen ? (
            <Minimize2 className="h-5 w-5" />
          ) : (
            <Maximize2 className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Floating Search and Filter Controls - Only in fullscreen */}
      {isMobileFullscreen && (
        <AnimatePresence> {/* No mode prop: allows multiple children to animate simultaneously */}
          {/* Floating Action Buttons */}
          <motion.div
            key="floating-action-buttons"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="absolute left-4 z-40 flex gap-2 top-[max(1rem,env(safe-area-inset-top))]"
          >
            {/* Search Toggle Button - Enhanced for mobile */}
            <Button
              onClick={onSearchToggle}
              size="sm"
              className={cn(
                "h-11 px-4 rounded-full shadow-xl backdrop-blur-sm",
                "border border-white/30 transition-all duration-300",
                "flex items-center gap-2 min-h-[44px] touch-manipulation",
                "font-semibold text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2",
                showFloatingSearch 
                  ? "bg-emerald-600 text-white shadow-emerald-600/25" 
                  : "bg-white/95 text-gray-800 hover:bg-white hover:shadow-xl"
              )}
              aria-label={showFloatingSearch ? "Close search" : "Search destinations"}
            >
              <Search className="h-4 w-4 shrink-0" />
              <span>Search</span>
            </Button>

            {/* Filter Toggle Button - Direct Modal Trigger */}
            {SHOW_HOMEPAGE_FILTERS && <Button
              onClick={onFilterToggle}
              size="sm"
              className={cn(
                "h-11 px-4 rounded-full shadow-xl backdrop-blur-sm",
                "border border-white/30 transition-all duration-300",
                "flex items-center gap-2 relative min-h-[44px] touch-manipulation",
                "font-semibold text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2",
                "bg-white/95 text-gray-800 hover:bg-white hover:shadow-xl"
              )}
              aria-label={hasActiveFilters ? "Filters active, open filter options" : "Open filters"}
            >
              <FilterIcon className="h-4 w-4 shrink-0" />
              <span>Filters</span>
              {hasActiveFilters && (
                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white shadow-sm animate-pulse" />
              )}
            </Button>}
          </motion.div>

          {/* Floating Search Bar - Mobile Fullscreen Optimized */}
          {showFloatingSearch && (
            <motion.div
              key="floating-search-bar"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="absolute left-4 right-4 z-40 w-[calc(100vw-2rem)] max-w-[320px] top-[max(5rem,calc(env(safe-area-inset-top)+4rem))]" // Below FAB row, respect safe area
            >
              {onSearch ? (
                <MobileFullscreenSearch
                  onSearch={onSearch}
                  placeholder="Search destinations, cities..."
                  onClose={onCloseSearch}
                />
              ) : (
                <div className="bg-white/96 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/60 p-4">
                  <div className="flex items-center gap-3 text-gray-500">
                    <Search className="h-5 w-5" />
                    <span>Search functionality not available</span>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
} 